import { z } from "zod";
import { customersApiSchema } from "../services/customersService";
import { productsApiSchema } from "../services/productsService";
import { usersApiSchema } from "../services/usersApi";
import { paginatedResponse } from "../utils";
import { reasonApiSchema } from "./reason";
import { statusApiSchema } from "./status";

// Domain type schemas

export const AgreementKindEnum = ((AgreementKindEnum) => {
    AgreementKindEnum.RESERVATION = "Reservation";
    AgreementKindEnum.PRE_INITIAL = "Preliminary";
    AgreementKindEnum.DEVELOPER = "Developer";
    AgreementKindEnum.SALES = "Sales";
    return AgreementKindEnum;
})({});

export const DEFAULT_AGREEMENT_KIND = AgreementKindEnum.RESERVATION;

export const AGREEMENT_KINDS = [
    {
        id: "a4adbfa4-e0bf-44fb-b2ab-593759f1b02d",
        key: AgreementKindEnum.RESERVATION,
        translationKey: "agreements.kinds.reservation",
    },
    {
        id: "0bd3bb5a-991e-472b-a8f4-6bbb8c146e73",
        key: AgreementKindEnum.PRE_INITIAL,
        translationKey: "agreements.kinds.preInitial",
    },
    {
        id: "65f2d569-98c0-4fdd-abee-95266f1d9245",
        key: AgreementKindEnum.DEVELOPER,
        translationKey: "agreements.kinds.developer",
    },
    {
        id: "7d593587-4c14-4c9e-b4c7-f86e1af10c32",
        key: AgreementKindEnum.SALES,
        translationKey: "agreements.kinds.sales",
    },
];

export const AGREEMENT_TYPES = {
    Agreement: "db6918ef-6d66-4cb3-b4f6-1ee569e2f6fd",
    Annex: "ecc0a422-35de-4744-b12e-ace4fdaf4d2e",
    Extension: "b77e2bd5-e6f7-4a38-a237-16441d7aa60e",
};

export const AGREEMENT_TYPE_KEYS = {
    Agreement: "Agreement",
    Annex: "Annex",
    Extension: "Extension",
};

export const AGREEMENT_PAYMENT_KINDS = {
    Reservation: "844ac0cc-9782-43ad-beca-617bf421b4e9",
    Tranche: "7786a777-c99c-4c70-bb40-8c37e84a186b",
    FinalSettlement: "49e3f3d8-0c2e-4839-b577-1db35a9f8424"
};

const AgreementKindSchema = z.object({
    id: z.string().uuid(),
    agrekind_key: z.string(),
    agrekind_name: z.string(),
    agrekind_name_eng: z.string(),
});

const AgreementTypeSchema = z.object({
    id: z.string().uuid(),
    agretype_key: z.string(),
    agretype_name: z.string(),
    agretype_name_eng: z.string(),
});

const AgreementPaymentSchema = z.object({
    id: z.string().uuid(),
    agrepay_agreement: z.string().uuid(),
    agrepay_plan_date: z.string().optional().nullable(),
    agrepay_notification_draft: z.string().uuid().optional().nullable(),
    agrepay_milestone: z.string().uuid().optional().nullable(),
    agrepay_due_date: z.string().optional().nullable(),
    agrepay_kind: z.string().uuid(),
    agrepay_payment_shift: z.number().optional().nullable(),
    agrepay_transfer_is_paid: z.boolean(),
    agrepay_notification_send_date: z.string().optional().nullable(),
    per_product_percentages: z.array(
        z.object({
            product_id: z.string().uuid(),
            percentage: z.coerce.number(),
        }),
    ),
    notification_body: z.string(),
    paid_amount: z.number().optional().nullable(),
    remaining_amount: z.number().optional().nullable(),
    is_paid: z.boolean().optional().nullable(),
});

const AgreementStatusSchema = statusApiSchema.status;

const AgreementPaymentKindSchema = z.object({
    id: z.string().uuid(),
    paymentkind_key: z.string(),
    paymentkind_name: z.string(),
    paymentkind_name_eng: z.string(),
});

const AgreementShareSchema = z.object({
    id: z.string().uuid(),
    customers: z.array(customersApiSchema.customer),
    percentage: z.coerce.number(),
});

const AgreementHistoryEntrySchema = z.object({
    agrelog_child: z.string().uuid().optional().nullable(),
    agrelog_parent: z.string().uuid(),
    agrelog_parent_number: z.string(),
    agrelog_child_number: z.string().optional().nullable(),
    agrelog_parent_kind: AgreementKindSchema,
    agrelog_child_kind: AgreementKindSchema.optional().nullable(),
    agrelog_child_type: AgreementTypeSchema.optional().nullable(),
    date_updated: z.string(),
    agrelog_note: z.string(),
});

const AgreementProductSchema = z.object({
    product: productsApiSchema.product,
    discount_proc: z.coerce.number(),
});

const AgreementCustomerSchema = z.object({
    customer: customersApiSchema.customer,
    agrecust_is_customer_jdg: z.boolean(),
    agrecust_is_customer_nip: z.boolean(),
    agrecust_is_foreigner: z.boolean(),
});

export const BankingSchema = z.object({
    id: z.string().optional().nullable(),
    banking_kind : z.string().optional().nullable(),
    banking_nr : z.string().optional().nullable(),
    banking_bank : z.string().optional().nullable()
});

const AgreementBbookingSchema = z.object({
    created: z.string(),
    agrebook_agreement: z.any().nullable().optional(),
    agrebook_booking: z.any().nullable().optional(),
});

const AgreementSchema = z.object({
    id: z.string().uuid(),
    agreement_nr: z.string(),
    agreement_name: z.string().nullable().optional(),
    agreement_date_signed: z.string().nullable().optional(),
    agreement_date_activation: z.string().nullable().optional(),
    agreement_date_expires: z.string().nullable().optional(),
    agreement_kind: AgreementKindSchema,
    agreement_type: AgreementTypeSchema,
    agreement_seller: usersApiSchema.seller.nullable().optional(),
    agreement_value_net: z.string().nullable().optional(), // Decimal as string
    agreement_value_vat: z.string().nullable().optional(), // Decimal as string
    agreement_value_gross: z.string().nullable().optional(), // Decimal as string
    agreement_draft: z.string().uuid().optional().nullable(),
    agreement_archive: z.string().nullable().optional(),
    agreement_broker_name: z.string().nullable().optional(),
    agreement_broker_percentage: z.number().nullable().optional(),
    agreement_note_sales: z.string(),
    agreement_status: statusApiSchema.status,
    agreement_status_reason: reasonApiSchema.reason.optional().nullable(),
    agreement_invest: z.string().uuid(),
    agreement_notarial_deed: z.string().optional().nullable(),
    agreement_notarial_plan_date: z.string().optional().nullable(),
    agreement_date_reception: z.string().optional().nullable(),
    agreement_notarial_sign_date: z.string().optional().nullable(),
    agreement_is_operator: z.boolean(),
    agreement_is_wpk: z.boolean(),
    agreement_is_loan: z.boolean(),
    agreement_loan_agency: z.boolean(),
    products: z.array(AgreementProductSchema),
    payments: z.array(AgreementPaymentSchema),
    shares: z.array(AgreementShareSchema),
    docs: z.array(z.any()), // TODO
    history: z.array(AgreementHistoryEntrySchema),
    reservation_agreement_id: z.string().uuid().nullable(),
    development_agreement_id: z.string().uuid().nullable(),
    preliminary_agreement_id: z.string().uuid().nullable(),
    sale_agreement_id: z.string().uuid().nullable(),
    created: z.string(),
    agreement_parent: z.string().uuid().nullable().optional(),
    customers: z.array(AgreementCustomerSchema),
    agreement_booking: AgreementBbookingSchema.nullable().optional(),
    agreement_final_settlement: z.boolean().nullable().optional(),
    is_investment_effective_footage_filled: z.boolean().nullable().optional(),
    reservation_payment_paid_amount: z.number().nullable().optional(),
    agreement_banking: BankingSchema.optional().nullable(),
    agreement_banking_withdrawal: BankingSchema.optional().nullable(),
});

const AgreementPaymentScheduleSchema = z.object({
    id: z.string().uuid(),
    schedule_name: z.string(),
    installments: z.array(
        z.object({
            installment_order: z.number(),
            installment_percentage: z.coerce.number(),
        }),
    ),
});

const PaymentScheduleInstallmentSchema = z.object({
    installment_order: z.number(),
    installment_percentage: z.coerce.number(),
});

const MilestoneSchema = z.object({
    id: z.string().uuid(),
    investment: z.string().uuid(),
    parent_name: z.string().nullable(),
    milestone_name: z.string(),
    milestone_responsible: z.string().nullable(),
    milestone_note: z.string().nullable(),
    milestone_is_for_payment: z.boolean(),
    milestone_payment_proc: z.number().int(),
    miledate_start: z.string().nullable(),
    miledate_start_real: z.string().nullable(),
    miledate_start_rev: z.number().int(),
    miledate_stop: z.string().nullable(),
    miledate_stop_real: z.string().nullable(),
    miledate_stop_rev: z.number().int(),
});

const DefaultPaymentScheduleResponseSchema = z.object({
    installments: z.array(PaymentScheduleInstallmentSchema),
});

const PaymentScheduleSchema = z.object({
    id: z.string().uuid(),
    schedule_name: z.string(),
    installments: z.array(PaymentScheduleInstallmentSchema),
});

// API request/response schemas

// Only selected fields from the full Agreement object
const CreateAgreementRequestSchema = z.object({
    agreement_date_signed: z.string().optional(),
    agreement_date_activation: z.string().optional().nullable(),
    agreement_date_expires: z.string().optional(),
    agreement_kind: z.string(),
    agreement_type: z.string().uuid(),
    agreement_seller: z.string().uuid(),
    agreement_archive: z.string(),
    agreement_broker_name: z.string(),
    agreement_broker_percentage: z.number(),
    agreement_note_sales: z.string(),
    agreement_notarial_deed: z.string().optional().nullable(),
    agreement_notarial_plan_date: z.string().optional().nullable(),
    agreement_date_reception: z.string().optional().nullable(),
    agreement_notarial_sign_date: z.string().optional().nullable(),
    agreement_is_operator: z.boolean(),
    agreement_is_wpk: z.boolean(),
    agreement_is_loan: z.boolean(),
    agreement_parent: z.string().uuid().optional().nullable(),
    source_booking: z.string().uuid().optional().nullable(),
    products: z.array(
        z.object({
            product: z.string().uuid(),
            discount_proc: z.number(),
        }),
    ),
    payments: z.array(
        z.object({
            agrepay_plan_date: z.string().optional().nullable(),
            agrepay_notification_draft: z.string().uuid().optional().nullable(),
            agrepay_milestone: z.string().uuid().optional().nullable(),
            agrepay_due_date: z.string().optional().nullable(),
            agrepay_kind: z.string().uuid(),
            agrepay_payment_shift: z.number().optional().nullable(),
            agrepay_transfer_is_paid: z.boolean(),
            per_product_percentages: z.array(
                z.object({
                    product_id: z.string().uuid(),
                    percentage: z.number().or(z.string()),
                }),
            ),
        }),
    ),
    shares: z.array(
        z.object({
            customers: z.array(z.string().uuid()),
            percentage: z.number(),
        }),
    ),
    customers: z.array(
        z.object({
            customer: z.string().uuid(),
            is_customer_jdg: z.boolean().optional(),
            is_customer_nip: z.boolean().optional(),
            is_foreigner: z.boolean().optional(),
        }),
    ),
});

const CreateUpdatePaymentScheduleRequestSchema = z.object({
    schedule_name: z.string(),
    installments: z.array(
        z.object({
            installment_order: z.number(),
            installment_percentage: z.number(),
        }),
    ),
});

const UpdateAgreementRequestSchema = CreateAgreementRequestSchema.extend({
    update_note: z.string().optional(),
});

const PartialUpdateAgreementRequestSchema = UpdateAgreementRequestSchema.partial();

const GetPaymentSchedulesResponseSchema = z.array(PaymentScheduleSchema);
const GetAgreementsResponseSchema = paginatedResponse(AgreementSchema);
const GetAgreementKindsResponseSchema = z.array(AgreementKindSchema);
const GetAgreementTypesResponseSchema = z.array(AgreementTypeSchema);

const GetAgreementPaymentSchedulesResponseSchema = paginatedResponse(
    AgreementPaymentScheduleSchema,
);

const GetAgreementPaymentKindsResponseSchema = z.array(AgreementPaymentKindSchema);
const GetMilestonesResponseSchema = z.array(MilestoneSchema);

const GetAgreementPaymentsResponseSchema = paginatedResponse(AgreementPaymentSchema);

const AgreementCancellationReasonSchema = z.object({
    id: z.string().uuid(),
    reason_name: z.string(),
    reason_name_eng: z.string(),
});

const CancelAgreementRequestSchema = z.object({
    reason_id: z.string().uuid(),
    status_note: z.string().optional().nullable(),
    return_reservation_payment: z.boolean().optional().nullable()  
});

const GetAgreementCancellationReasonsResponseSchema = AgreementCancellationReasonSchema.array();
const GetAgreementStatusResponseSchema = AgreementStatusSchema.array();

const SendPaymentNotificationRequestSchema = z.object({
    agrepay: z.string().uuid(),
    subject: z.string(),
    body: z.string(),
    due_date: z.string().optional().nullable(),
});

export const agreementsApiSchema = {
    agreement: AgreementSchema,
    agreementStatusesResponse: GetAgreementStatusResponseSchema,
    agreementCustomer: AgreementCustomerSchema,
    agreementPayment: AgreementPaymentSchema,
    agreementKind: AgreementKindSchema,
    agreementType: AgreementTypeSchema,
    agreementPaymentKind: AgreementPaymentKindSchema,
    agreementShare: AgreementShareSchema,
    agreementHistoryEntry: AgreementHistoryEntrySchema,
    agreementPaymentSchedule: AgreementPaymentScheduleSchema,
    milestone: MilestoneSchema,
    defaultPaymentScheduleResponse: DefaultPaymentScheduleResponseSchema,
    createAgreementRequest: CreateAgreementRequestSchema,
    getAgreementsResponse: GetAgreementsResponseSchema,
    getAgreementPaymentSchedulesResponse: GetAgreementPaymentSchedulesResponseSchema,
    getAgreementPaymentKindsResponse: GetAgreementPaymentKindsResponseSchema,
    getMilestonesResponse: GetMilestonesResponseSchema,
    paymentSchedule: PaymentScheduleSchema,
    paymentScheduleInstallment: PaymentScheduleInstallmentSchema,
    createUpdatePaymentScheduleRequest: CreateUpdatePaymentScheduleRequestSchema,
    getPaymentSchedulesResponse: GetPaymentSchedulesResponseSchema,
    updateAgreementRequest: UpdateAgreementRequestSchema,
    partialUpdateAgreementRequest: PartialUpdateAgreementRequestSchema,
    getAgreementKindsResponse: GetAgreementKindsResponseSchema,
    getAgreementTypesResponse: GetAgreementTypesResponseSchema,
    getAgreementPaymentsResponse: GetAgreementPaymentsResponseSchema,
    agreementCancellationReason: AgreementCancellationReasonSchema,
    cancelAgreementRequest: CancelAgreementRequestSchema,
    getAgreementCancellationReasonsResponse: GetAgreementCancellationReasonsResponseSchema,
    sendPaymentNotificationRequest: SendPaymentNotificationRequestSchema,
};

export const NotificationKindsForPaymentEnum = ((NotificationKindsForPaymentEnum) => {
    NotificationKindsForPaymentEnum.TRANCHE = "tranche";
    NotificationKindsForPaymentEnum.FINAL_SETTLEMENT = "final_settlement";
    return NotificationKindsForPaymentEnum;
})({});

export const NOTIFICATION_FOR_PAYMENTS_KINDS = [
    {
        id: "2b9e0a18-568f-4cfd-b7c8-862fa841c732",
        key: NotificationKindsForPaymentEnum.TRANCHE,
    },
    {
        id: "f1a1105a-417d-4343-b761-96d265f9acd5",
        key: NotificationKindsForPaymentEnum.FINAL_SETTLEMENT,
    },
]